import React, { useCallback, useMemo } from "react";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { FinancialDocument } from "@common/types/financialDocument";
import { FinancialDocumentBulkActionTypes } from "@common/constants";
import { Counterparty } from "@common/types/counterparty";
import { UploadIcon } from "@icons";
import { useScope } from "@state/scopeContext.lib";
import { Link } from "@design-system/Link/Link";
import { useFinancialDocumentsQuery } from "@components/documents/useFinancialDocumentsQuery";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { FinancialDocumentAttachment } from "@components/documents/FinancialDocumentAttachment/FinancialDocumentAttachment";
import { DocumentSelectVariant } from "@components/documents/FinancialDocumentSelect/financialDocumentSelectViewConfig";
import {
    useFinancialDocumentBulkActionMutation,
    useUpdateDocumentCounterpartyMutation,
} from "@api/financialDocument.api";
import styles from "./CounterpartyDetailsPanel.module.scss";

const MATCH_EXISTING_INITIAL_FILTERS = { hasNoCounterparty: true };

interface Props {
    counterparty: Counterparty;
}

export const CounterpartyAttachments: React.FC<Props> = ({
    counterparty: cp,
}) => {
    const { workspaceId } = useScope();
    const { error: toastError } = useToaster();

    const { data: financialDocumentsResponse } = useFinancialDocumentsQuery({
        queryParams: {
            filters: {
                counterpartiesIds: [cp.id],
            },
            // Hardcoded because we don't expect more than 100 documents
            // attached to a single counterparty.
            page: 0,
            limit: 100,
            workspaceId: workspaceId ?? "",
        },
        additionalParams: { enabled: !!workspaceId },
    });

    const attachedDocuments = useMemo(
        () => financialDocumentsResponse?.data ?? [],
        [financialDocumentsResponse],
    );
    const attachedDocumentIds = useMemo(
        () => attachedDocuments.map((document) => document.id),
        [attachedDocuments],
    );

    const { mutate: updateDocumentCounterparty } =
        useUpdateDocumentCounterpartyMutation();

    const { mutateAsync: runBulkAction } =
        useFinancialDocumentBulkActionMutation({
            onError: () => {
                toastError("Failed to attach documents to counterparty");
            },
        });

    const attachDocument = useCallback(
        (financialDocumentId: number) => {
            updateDocumentCounterparty({
                financialDocumentId,
                counterpartyId: cp.id,
            });
        },
        [cp, updateDocumentCounterparty],
    );

    const attachDocuments = useCallback(
        async (financialDocumentIds: number[]) => {
            if (!workspaceId || financialDocumentIds.length === 0) {
                return;
            }
            try {
                await runBulkAction({
                    workspaceId,
                    financialDocumentIds,
                    action: {
                        type: FinancialDocumentBulkActionTypes.ASSIGN_COUNTERPARTY,
                        counterpartyId: cp.id,
                    },
                });
            } catch {
                // Surfaced to the user via the mutation's onError toast.
            }
        },
        [cp, workspaceId, runBulkAction],
    );

    const detachDocument = useCallback(
        (financialDocumentId: number) => {
            updateDocumentCounterparty({
                financialDocumentId,
                counterpartyId: null,
            });
        },
        [updateDocumentCounterparty],
    );

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            await attachDocuments(
                uploadedDocuments.map((document) => document.id),
            );
        },
        [attachDocuments],
    );

    return (
        <div className={styles.value}>
            <FinancialDocumentUploadProvider
                onCompleted={handleUploadCompleted}
            >
                <AddDocument
                    trigger={
                        <Link size="xs" leftIcon={<UploadIcon />}>
                            Upload
                        </Link>
                    }
                    attachDocument={attachDocument}
                    attachDocuments={attachDocuments}
                    attachedDocumentIds={attachedDocumentIds}
                    variant={DocumentSelectVariant.COUNTERPARTY_ATTACHMENT}
                    initialFilters={MATCH_EXISTING_INITIAL_FILTERS}
                />
            </FinancialDocumentUploadProvider>
            {attachedDocuments.map((document) => (
                <FinancialDocumentAttachment
                    key={document.id}
                    financialDocument={document}
                    detachDocument={detachDocument}
                />
            ))}
        </div>
    );
};
